// Suppress-delivery sentinel for scheduled tasks (ENG-4463, ENG-4480).
//
// Scheduled tasks with a conditional instruction like
//   "DO NOT notify me unless X"
// were being delivered verbatim every run ("Nothing urgent." + attribution
// footer) because the agent always produced a non-empty final response and
// the delivery pipeline shipped whatever it got.
//
// The fix is a contract the agent opts into: respond with exactly
// <no-delivery/> on a single line when the conditional isn't met. The
// preamble in prompt-wrapper.ts teaches it; the helpers below are the gate
// the delivery pipeline uses to honour it.
//
// Failure modes the strict-equality match (ENG-4463) left open, all fixed
// here as ENG-4480:
//   1. Agent emits sentinel + explanatory `[notes]` block -> recipient sees
//      the literal `<no-delivery/>` token. Confusing, leaks an internal.
//   2. Agent mixes sentinel mid-message with real deliverable content
//      (seen in the wild: "Nothing urgent. ... <no-delivery/> ...").
//   3. Multiple sentinel tokens scattered through output.
//
// Resolution:
//   - `classifyOutput()` returns { action, deliverable, suppressedNotes }.
//     - action=suppress when the only non-whitespace content IS sentinels
//       (even with trailing notes that can be logged out-of-band).
//     - action=strip when sentinels appear alongside other real content.
//       Keep the real content, drop every sentinel token, emit the cleaned
//       string for delivery.
//     - action=deliver when no sentinel is present.
//   - `isSuppressOutput()` remains for call sites that only need the
//     boolean decision.

/** The literal token agents return to suppress delivery. */
export const SUPPRESS_SENTINEL = '<no-delivery/>';

/** Regex form of the sentinel, escaped so `.` stays literal and `/` works
 *  inside a character-class-free pattern. Global for replaceAll.
 *
 *  ENG-6084: also swallow up to three surrounding inline backticks. The
 *  teaching prose displays the token as `<no-delivery/>` (markdown code
 *  formatting), so agents sometimes emit it backtick-wrapped; pre-fix the
 *  bare-token regex left the backticks behind as "real content" and a
 *  backticked sentinel ALONE classified as strip — delivering stray
 *  backticks instead of suppressing. */
const SENTINEL_REGEX = /`{0,3}<no-delivery\/>`{0,3}/g;

export interface OutputClassification {
  /**
   * - 'suppress': delivery pipeline should not send. Agent signalled opt-out.
   * - 'strip':    deliver the cleaned string. Sentinel was accidental noise
   *               mixed in with real content — don't ship it as a token.
   * - 'deliver':  output has no sentinel, pass through unchanged.
   */
  action: 'suppress' | 'strip' | 'deliver';
  /** Cleaned message to deliver. Only meaningful when action === 'deliver'
   *  or 'strip'. Empty string when action === 'suppress'. */
  deliverable: string;
  /** For 'suppress' paths: any non-sentinel content the agent emitted
   *  alongside the sentinel. Never delivered — forwarded to the manager log
   *  so operators can see why the agent opted out. Empty when agent emitted
   *  the sentinel alone. */
  suppressedNotes: string;
}

/**
 * Classify scheduled-task output into suppress / strip / deliver.
 *
 * The decision rule:
 *   1. null / undefined / whitespace-only => suppress (empty, nothing to send).
 *   2. No sentinel token anywhere => deliver as-is.
 *   3. Sentinel present AND the non-sentinel remainder is whitespace-only =>
 *      suppress. Anything that looked like notes (`[notes] ...`, bullet
 *      lists of assumptions, etc.) lives in `suppressedNotes` for logging.
 *   4. Sentinel present AND the non-sentinel remainder has real content =>
 *      strip the sentinel(s) and deliver the cleaned text. The agent
 *      emitted a real deliverable; the sentinel was a habit/mistake.
 */
export function classifyOutput(output: string | null | undefined): OutputClassification {
  if (output == null) {
    return { action: 'suppress', deliverable: '', suppressedNotes: '' };
  }
  const trimmed = output.trim();
  if (trimmed.length === 0) {
    return { action: 'suppress', deliverable: '', suppressedNotes: '' };
  }

  if (!SENTINEL_REGEX.test(trimmed)) {
    // Rebuild regex's lastIndex (stateful because it's global); also pass
    // the original string (untrimmed) so downstream formatting survives.
    SENTINEL_REGEX.lastIndex = 0;
    return { action: 'deliver', deliverable: output, suppressedNotes: '' };
  }
  SENTINEL_REGEX.lastIndex = 0;

  const withoutSentinel = trimmed.replace(SENTINEL_REGEX, '').trim();

  if (withoutSentinel.length === 0) {
    return { action: 'suppress', deliverable: '', suppressedNotes: '' };
  }

  // Heuristic: if the non-sentinel remainder looks purely like operator
  // notes (leading `[notes]` marker, or nothing but a bulleted assumptions
  // block), treat this as a suppress + log-notes case. Anything else is a
  // genuine deliverable the agent happened to spoil with a sentinel.
  if (looksLikeNotesOnly(withoutSentinel)) {
    return { action: 'suppress', deliverable: '', suppressedNotes: withoutSentinel };
  }

  // Strip sentinels from the original output (not the trimmed form — we
  // want to preserve the agent's intended formatting) and collapse the
  // resulting run of blank lines so we don't ship "real content\n\n\n\n".
  const cleaned = output.replace(SENTINEL_REGEX, '').replace(/\n{3,}/g, '\n\n').trim();
  return { action: 'strip', deliverable: cleaned, suppressedNotes: '' };
}

/**
 * Convenience wrapper for existing call sites that only need the boolean
 * suppress/deliver decision. Prefer `classifyOutput` for new code so the
 * strip behaviour is reachable.
 */
export function isSuppressOutput(output: string | null | undefined): boolean {
  return classifyOutput(output).action === 'suppress';
}

/** Patterns the classifier treats as "non-deliverable residue" — if every
 *  non-empty line in the remainder matches one of these, the sentinel IS
 *  the real message and the remainder is just bookkeeping. Anything else
 *  is treated as a genuine deliverable the agent spoiled with a stray
 *  sentinel, and we strip rather than suppress. */
const NON_DELIVERABLE_REMAINDER_PATTERNS: RegExp[] = [
  /^\[notes\]/i,                          // Operator-facing notes block.
  /^[—–-]\s*scheduled by\b/i,             // Default delivery-pipeline footer.
  /^sent (?:from|via)\b/i,                // Mobile-style signatures.
  /^—?\s*automated (?:brief|report|message)\b/i,
  // ENG-6084: stray code-fence lines left behind when the agent wrapped the
  // sentinel in a fenced block (```\n<no-delivery/>\n```) — the fence lines
  // are formatting residue, not a deliverable.
  /^`{1,3}\w*$/,
];

/** Does the non-sentinel remainder look like it was only attribution /
 *  notes / footer text — i.e. emitting the full remainder as its own
 *  message would leak an internal or produce a standalone footer without
 *  any substance? The preamble teaches agents to put assumptions under a
 *  `[notes]` line; the delivery pipeline appends an attribution footer.
 *  Both end up mixed with the sentinel in practice. */
function looksLikeNotesOnly(remainder: string): boolean {
  const lines = remainder.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return false;
  return lines.every((line) =>
    NON_DELIVERABLE_REMAINDER_PATTERNS.some((pattern) => pattern.test(line)),
  );
}
